/* eslint-disable react/no-unstable-nested-components */
import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import logger from '@/utils/logger';
import {
  ArrowLeft, Phone, Mail, MapPin, Calendar, Users, DollarSign,
  Tag, Clock, Pencil, Save, X as XIcon, MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGES = ['new', 'contacted', 'proposal_sent', 'negotiation', 'follow_up', 'won', 'lost', 'ghosted'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const STAGE_COLORS = {
  new: 'bg-blue-100 text-blue-700', contacted: 'bg-amber-100 text-amber-700',
  proposal_sent: 'bg-purple-100 text-purple-700', negotiation: 'bg-pink-100 text-pink-700',
  follow_up: 'bg-cyan-100 text-cyan-700', won: 'bg-green-100 text-green-700',
  lost: 'bg-red-100 text-red-700', ghosted: 'bg-slate-100 text-slate-500',
};

export default function MetaAdsLeadDetail({ leadId, onBack }) {
  const { getAuthHeaders, admin } = useAdmin();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('info');
  const [note, setNote] = useState('');
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [showRaw, setShowRaw] = useState(false);

  const headers = getAuthHeaders();
  const hdrs = { ...headers, 'Content-Type': 'application/json' };

  const fetchLead = useCallback(async () => {
    try {
      const hdrs = getAuthHeaders();
      const [lRes, uRes] = await Promise.all([
        fetch(`${API_URL}/api/meta-ads/leads/${leadId}`, { headers: hdrs }),
        fetch(`${API_URL}/api/meta-ads/users`, { headers: hdrs }),
      ]);
      const lData = await lRes.json();
      setLead(lData.lead);
      setActivities(lData.activities || []);
      setUsers((await uRes.json()).users || []);
    } catch (e) { logger.error(e); }
    finally { setLoading(false); }
  }, [leadId, getAuthHeaders]);

  useEffect(() => { fetchLead(); }, [fetchLead]);

  const updateField = async (field, value) => {
    try {
      await fetch(`${API_URL}/api/meta-ads/leads/${leadId}`, {
        method: 'PATCH', headers: hdrs, body: JSON.stringify({ [field]: value }),
      });
      setEditing(null);
      fetchLead();
      toast.success('Updated');
    } catch (e) { toast.error('Update failed'); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await fetch(`${API_URL}/api/meta-ads/leads/${leadId}/note`, {
        method: 'POST', headers: hdrs, body: JSON.stringify({ note: note.trim() }),
      });
      setNote('');
      fetchLead();
      toast.success('Note added');
    } catch (e) { toast.error('Failed to add note'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400">Loading...</div>;
  if (!lead) return <div className="text-center py-12 text-slate-400">Lead not found</div>;

  const InfoField = ({ icon: Icon, label, field, value }) => {
    const isEditing = editing === field;
    return (
      <div className="flex items-start gap-3 py-2">
        <Icon size={16} className="text-slate-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-400 mb-0.5">{label}</p>
          {isEditing ? (
            <div className="flex items-center gap-2">
              <input type="text" value={editVal}
                onChange={e => setEditVal(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && updateField(field, editVal)}
                className="border rounded px-2 py-1 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <button onClick={() => updateField(field, editVal)} className="text-green-600 hover:text-green-700">
                <Save size={14} />
              </button>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-600">
                <XIcon size={14} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <p className="text-sm text-slate-900">{value || '—'}</p>
              <button onClick={() => { setEditing(field); setEditVal(value || ''); }}
                className="text-slate-300 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity">
                <Pencil size={12} />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="lead-detail-page">
      {/* Header */}
      <div className="bg-white rounded-xl border p-5">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-blue-600 mb-3" data-testid="back-to-leads">
          <ArrowLeft size={16} /> Back to leads
        </button>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{lead.full_name || 'Unknown Lead'}</h2>
            <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-slate-500">
              {lead.phone && <span className="flex items-center gap-1"><Phone size={13} />{lead.phone}</span>}
              {lead.email && <span className="flex items-center gap-1"><Mail size={13} />{lead.email}</span>}
            </div>
            <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
              {lead.campaign_name && <span>{lead.campaign_name}</span>}
              {lead.form_name && <><span>→</span><span>{lead.form_name}</span></>}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={lead.lead_stage} onChange={e => updateField('lead_stage', e.target.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border-0 cursor-pointer capitalize ${STAGE_COLORS[lead.lead_stage] || ''}`}
              data-testid="stage-select">
              {STAGES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
            <select value={lead.priority} onChange={e => updateField('priority', e.target.value)}
              className="border rounded px-2 py-1.5 text-xs font-medium capitalize bg-white cursor-pointer"
              data-testid="priority-select">
              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={lead.assigned_to || ''} onChange={e => updateField('assigned_to', e.target.value || null)}
              className="border rounded px-2 py-1.5 text-xs bg-white cursor-pointer" data-testid="assign-select">
              <option value="">Unassigned</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white rounded-xl border p-1">
        {[
          { key: 'info', label: 'Lead Info' },
          { key: 'activity', label: 'Activity & Notes' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.key ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'info' && (
        <div className="grid md:grid-cols-2 gap-4">
          {/* Contact Details */}
          <div className="bg-white rounded-xl border p-5 group">
            <h3 className="font-semibold text-slate-900 mb-3">Contact Details</h3>
            <InfoField icon={Users} label="Full Name" field="full_name" value={lead.full_name} />
            <InfoField icon={Mail} label="Email" field="email" value={lead.email} />
            <InfoField icon={Phone} label="Phone" field="phone" value={lead.phone} />
            <InfoField icon={Phone} label="WhatsApp" field="whatsapp_number" value={lead.whatsapp_number} />
          </div>

          {/* Travel Details */}
          <div className="bg-white rounded-xl border p-5 group">
            <h3 className="font-semibold text-slate-900 mb-3">Travel Details</h3>
            <InfoField icon={MapPin} label="Destination" field="destination" value={lead.destination} />
            <InfoField icon={Calendar} label="Travel Date" field="travel_date" value={lead.travel_date} />
            <InfoField icon={Users} label="Travellers" field="num_travellers" value={lead.num_travellers} />
            <InfoField icon={DollarSign} label="Budget" field="budget" value={lead.budget} />
            <InfoField icon={Tag} label="Trip Type" field="trip_type" value={lead.trip_type} />
          </div>

          {/* Meta Info */}
          <div className="bg-white rounded-xl border p-5 md:col-span-2">
            <button onClick={() => setShowRaw(!showRaw)}
              className="font-semibold text-slate-900 flex items-center gap-2">
              Meta Info
              <span className="text-xs text-slate-400 font-normal">{showRaw ? '(collapse)' : '(expand)'}</span>
            </button>
            {showRaw && (
              <div className="mt-3 space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-slate-400">Campaign:</span> {lead.campaign_name || '—'}</div>
                  <div><span className="text-slate-400">Form:</span> {lead.form_name || '—'}</div>
                  <div><span className="text-slate-400">Ad:</span> {lead.ad_name || '—'} {lead.ad_id && `(${lead.ad_id})`}</div>
                  <div><span className="text-slate-400">Meta Lead ID:</span> {lead.meta_lead_id || '—'}</div>
                  <div><span className="text-slate-400">Meta Created:</span> {lead.meta_created_time || '—'}</div>
                  <div><span className="text-slate-400">Synced At:</span> {lead.synced_at || '—'}</div>
                </div>
                {lead.raw_field_data && Object.keys(lead.raw_field_data).length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-slate-400 mb-1">Raw Form Data:</p>
                    <pre className="bg-slate-50 rounded-lg p-3 text-xs overflow-x-auto">
                      {JSON.stringify(lead.raw_field_data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'activity' && (
        <div className="space-y-4">
          {/* Add note */}
          <div className="bg-white rounded-xl border p-5">
            <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <MessageSquare size={16} /> Add Note
            </h3>
            <textarea value={note} onChange={e => setNote(e.target.value)}
              placeholder="Write a note about this lead..."
              className="w-full border rounded-lg p-3 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              data-testid="note-input"
            />
            <div className="flex justify-end mt-2">
              <Button size="sm" onClick={addNote} disabled={!note.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-note-btn">
                <Save size={14} className="mr-1" /> Save Note
              </Button>
            </div>
          </div>

          {/* Activity Timeline */}
          <div className="bg-white rounded-xl border p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Activity Timeline</h3>
            {activities.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No activity yet</p>
            ) : (
              <div className="space-y-4">
                {activities.map(a => (
                  <div key={a.id} className="flex gap-3 text-sm" data-testid={`activity-${a.id}`}>
                    <div className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-slate-700">
                        <span className="font-medium capitalize">{a.action_type.replace(/_/g, ' ')}</span>
                        {a.old_value && a.new_value && (
                          <span className="text-slate-400"> from <em>{a.old_value}</em> to <em>{a.new_value}</em></span>
                        )}
                        {!a.old_value && a.new_value && (
                          <span className="text-slate-400">: {a.new_value}</span>
                        )}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {a.performed_by_name || 'System'} · {a.created_at ? new Date(a.created_at).toLocaleString() : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes display */}
          {lead.notes && (
            <div className="bg-white rounded-xl border p-5">
              <h3 className="font-semibold text-slate-900 mb-3">Notes</h3>
              <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{lead.notes}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
